﻿import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { TbCheck, TbFileText, TbArrowRight } from "react-icons/tb";
import { PadFlowStoryboardPopup } from "src/pages/Invoices/PadFlowStoryboardPopup";
import { apiRequest } from "src/services/apiClient";
import { OnboardingChecklist } from "./OnboardingChecklist";
import "./onboardingModal.scss";

export { GettingStartedPopup } from "./GettingStartedPopup";

interface Props {
    onComplete: (avvSignedAt: string) => void;
}

export default function OnboardingModal({ onComplete }: Props) {
    const { t } = useTranslation();
    const [step, setStep] = useState(1);
    const [avvChecked, setAvvChecked] = useState(false);
    const [signedBy, setSignedBy] = useState("");
    const [signing, setSigning] = useState(false);

    const canSign = avvChecked && signedBy.trim().length >= 2;

    const handleSignAvv = async () => {
        if (!canSign || signing) return;
        setSigning(true);
        try {
            const res = await apiRequest<{ avvSignedAt: string }>("/sign-avv", {
                method: "POST",
                body: JSON.stringify({ signedBy: signedBy.trim() }),
            }, { "Content-Type": "application/json" });
            setStep(2);
            (handleSignAvv as any)._avvSignedAt = res.avvSignedAt;
        } catch {
            // keep step 1 on failure
        } finally {
            setSigning(false);
        }
    };

    const handleFinish = () => {
        onComplete((handleSignAvv as any)._avvSignedAt ?? new Date().toISOString());
    };

    return createPortal(
        <div className="onboarding-overlay">
            <div className={`onboarding-modal${step === 2 ? " onboarding-modal--wide" : step === 3 ? " onboarding-modal--checklist" : ""}`}>
                <div className="onboarding-modal__steps">
                    {[1, 2, 3].map((n) => (
                        <div
                            key={n}
                            className={`onboarding-modal__step-dot${step === n ? " onboarding-modal__step-dot--active" : step > n ? " onboarding-modal__step-dot--done" : ""}`}
                        >
                            {step > n ? <TbCheck /> : n}
                        </div>
                    ))}
                </div>

                {step === 1 && (
                    <div className="onboarding-modal__pane">
                        <div className="onboarding-modal__icon onboarding-modal__icon--avv">
                            <TbFileText />
                        </div>
                        <h2 className="onboarding-modal__title">{t("onboarding.avvTitle")}</h2>
                        <p className="onboarding-modal__desc">{t("onboarding.avvDesc")}</p>
                        <label className="onboarding-modal__check-label">
                            <input
                                type="checkbox"
                                checked={avvChecked}
                                onChange={(e) => setAvvChecked(e.target.checked)}
                            />
                            <span>
                                {t("onboarding.avvAccept")}{" "}
                                <a href="/legal#avv" target="_blank" rel="noreferrer">
                                    {t("onboarding.avvLink")}
                                </a>
                                .
                            </span>
                        </label>
                        <div className="onboarding-modal__signature">
                            <label className="onboarding-modal__signature-label">
                                {t("onboarding.avvSignatureLabel")}
                            </label>
                            <input
                                className="onboarding-modal__signature-input"
                                type="text"
                                placeholder={t("onboarding.avvSignaturePlaceholder")}
                                value={signedBy}
                                onChange={(e) => setSignedBy(e.target.value)}
                                autoComplete="name"
                            />
                            <p className="onboarding-modal__signature-hint">
                                {t("onboarding.avvSignatureHint")}
                            </p>
                        </div>
                        <button
                            className="onboarding-modal__btn onboarding-modal__btn--primary"
                            disabled={!canSign || signing}
                            onClick={handleSignAvv}
                        >
                            {signing ? "…" : t("onboarding.avvSign")}
                            <TbArrowRight />
                        </button>
                    </div>
                )}

                {step === 2 && (
                    <div className="onboarding-modal__pane onboarding-modal__pane--flow">
                        <div className="onboarding-modal__flow-wrap">
                            <PadFlowStoryboardPopup onClose={() => {}} embedded />
                        </div>
                        <p className="onboarding-modal__flow-note">{t("onboarding.flowOtherWays")}</p>
                        <div className="onboarding-modal__nav">
                            <button
                                className="onboarding-modal__btn onboarding-modal__btn--primary"
                                onClick={() => setStep(3)}
                            >
                                {t("onboarding.next")}
                                <TbArrowRight />
                            </button>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="onboarding-modal__pane">
                        <h2 className="onboarding-modal__title">{t("onboarding.gettingStartedTitle")}</h2>
                        <p className="onboarding-modal__desc">{t("onboarding.gettingStartedDesc")}</p>
                        <OnboardingChecklist onNavigate={handleFinish} />
                        <div className="onboarding-modal__nav">
                            <button
                                className="onboarding-modal__btn onboarding-modal__btn--ghost"
                                onClick={() => setStep(2)}
                            >
                                {t("onboarding.back")}
                            </button>
                            <button
                                className="onboarding-modal__btn onboarding-modal__btn--primary"
                                onClick={handleFinish}
                            >
                                <TbCheck /> {t("onboarding.finish")}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>,
        document.body,
    );
}
